import {Box, Card, Typography, useTheme, IconButton} from "@mui/material"
import {CheckBoxOutlineBlankOutlined, CheckBoxOutlined} from "@mui/icons-material"
import useIsMacOS from "../../hooks/useIsMacOS"
import {useDispatch, useSelector} from "react-redux"
import {
	removeTopLevelNotePermission,
	selectUserNoteRestrictions,
	setTopLevelNotePermission
} from "../../reducers/manageWorkspaceSlice"

/**
 * Represents the UserNoteRestrictions for a top-level note in the current workspace.
 *
 * @param note - target Note
 * @param readonly {boolean} - is the target user "read-only" in this workspace?
 * @returns {JSX.Element}
 * @constructor
 */
export default function NoteRestrictionBreadcrumb({note, readonly}) {
	const selectedNotes = useSelector(selectUserNoteRestrictions)
	const dispatch = useDispatch()
	// a NoteRestrictionBreadcrumb is "selected" if it has a permission type associated with the note
	const isSelected = selectedNotes[note.id] !== undefined
	const isEdit = isSelected && selectedNotes[note.id] === "edit"
	const theme = useTheme()
	let read = isSelected
	let edit = readonly ? false : (isSelected && isEdit)
	const setRead = () => {
		if(!isSelected || isSelected && isEdit) {
			dispatch(setTopLevelNotePermission({noteId: note.id, newPermissionType: "read"}))
		}

		if(isSelected && !isEdit) {
			dispatch(removeTopLevelNotePermission({noteId: note.id}))
		}
	}

	const setEdit = () => {
		if(isEdit) {
			dispatch(removeTopLevelNotePermission({noteId: note.id}))
		}

		if(!isSelected) {
			dispatch(setTopLevelNotePermission({noteId: note.id, newPermissionType: "edit"}))
		}
	}
	const toggleSelected = () => {
		if(isSelected) {
			dispatch(removeTopLevelNotePermission({noteId: note.id}))
		} else if(readonly) {
			dispatch(setTopLevelNotePermission({noteId: note.id, newPermissionType: "read"}))
		} else {
			dispatch(setTopLevelNotePermission({noteId: note.id, newPermissionType: "edit"}))
		}
	}

	return (
		<Box sx={{display: "flex", gap: "1rem", alignItems: "center", width: "100%", justifyContent: "flex-start"}}>
			<Box sx={{display: "flex", width: "100%"}}>
				<Card
					onClick={toggleSelected}
					variant={"note"}
					sx={{
						width: "100%",
						display: "flex",
						alignItems: "center",
						padding: "0.5rem",
						color: "RBOneFont.main",
						backgroundColor: "notes.crumbsBackground",
						borderLeft: "5px solid",
						borderColor: "notes.dark",
						lineHeight: "1rem",
						fontSize: "1rem",
						...((read || edit) && {
							borderColor: `${theme.palette.primary.main}`,
						}),
					}}
				>
					<Typography
						variant={"body1"}
						sx={{
							marginBottom: 0,
							wordBreak: "break-word",
							fontSize: "1rem",
						}}
						paragraph
					>{note.content}</Typography>
				</Card>
				<PermissionsCheckBox isSelected={read} setIsSelected={setRead}/>
				<PermissionsCheckBox isSelected={edit} setIsSelected={setEdit} readonly={readonly}/>
			</Box>
		</Box>
	)
}

function PermissionsCheckBox({isSelected, setIsSelected, readonly}) {
	const isMacOS = useIsMacOS()
	return (
		<IconButton
			sx={{
				minWidth: isMacOS ? "38px" : "24px",
				height: "unset"
			}}
			disabled={readonly === true}
			onClick={() => setIsSelected(!isSelected)}
		>
			{ isSelected ?
				<CheckBoxOutlined fontSize={"small"}/>
				:
				<CheckBoxOutlineBlankOutlined fontSize={"small"} />
			}
		</IconButton>
	)
}